import { useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BookLink } from '@todo/web-ui/components/book-link';
import { DetailLayout } from '@todo/web-ui/components/detail-layout';
import { EventCard } from '@todo/web-ui/components/event-card';
import { FaqList } from '@todo/web-ui/components/faq-list';
import { EventHotels, HotelsMapFrame } from '@todo/web-ui/components/hotels';
import { NavLink } from '@todo/web-ui/components/nav-link';
import { NotFoundScreen } from '@todo/web-ui/components/not-found-screen';
import { Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { CardGrid, Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { formatEventPrice, localePath, partnerName } from '@todo/web-ui/lib/format';
import { RELATED_EVENTS, relatedEventsSeedKey } from '@/lib/prefetch';
import { isEventPast } from '@todo/core/date-range';
import { EVENT_SHOW_BUCKET } from '@todo/core/detail/event-detail';
import type { EventShow, ShowDate } from '@todo/core/event-show';
import { useEventShow } from '@todo/core/hooks/use-event';
import {
  localizedDescription,
  localizedSeoTitle,
  localizedSummary,
  localizedTitle,
} from '@todo/core/i18n/event-content';
import { formatDateCompact, formatDateLong, formatTimeRange } from '@todo/core/i18n/format';
import { SUPPORTED_LOCALES, type Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { usableImageUrl } from '@todo/core/image-url';
import { LIST_BUCKET } from '@todo/core/list-queries';
import { absoluteUrl, breadcrumbJsonLd, eventsCrumb, faqPageJsonLd, seoTitle, truncate } from '@todo/core/seo';
import { MAX_JSON_LD_DATES, eventJsonLd } from '@todo/core/seo/event-jsonld';
import { getPrefetched, prefetchElementId, serializePrefetch } from '@todo/core/static-prefetch';
import type { DbEvent } from '@todo/core/supabase';
import { SITE } from '@site';

// The tour-facts look for the page's lists (highlights, dates).
import factsCss from '@todo/web-ui/components/tour-facts.module.css';

import css from './events.module.css';

// One page per show (every date of a Platinumlist event shares a show_slug),
// prerendered from @todo/core's build-time fetch. The slug is the URL and stays
// for as long as the show does — a weekly brunch keeps its page, and its links,
// for years — so it must never be changed.
export { generateEventStaticParams as generateStaticParams } from '@todo/core/detail/event-detail';

/** Dates listed on the page (the JSON-LD has its own cap, MAX_JSON_LD_DATES). */
const MAX_LISTED_DATES = 12;

/**
 * A source text this short says next to nothing of its own: without
 * highlights or FAQ facts written from it, the page is a thin copy of the
 * ticket page and stays out of the index.
 */
const THIN_SOURCE_CHARS = 300;

export default function EventShowPage() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { show, loading } = useEventShow(slug);

  if (!show) return loading ? <Page>{null}</Page> : <NotFoundScreen />;
  return <ShowScreen show={show} />;
}

/** Whether the page has copy of its own worth indexing in every language. */
function isIndexable(event: DbEvent): boolean {
  if (isEventPast(event)) return false;
  // Every locale's copy, or the language versions would be English duplicates.
  if (!SUPPORTED_LOCALES.every((l) => event.translations?.[l]?.description)) return false;
  const en = event.translations?.en;
  const thin = (event.description ?? '').length < THIN_SOURCE_CHARS && !en?.highlights?.length && !en?.faq?.length;
  return !thin;
}

/**
 * A slot offer's "end" is often the end of its sale day (afternoon tea from
 * 14:00 "to 23:59"): past this span only the start time is shown.
 */
const MAX_SHOWN_SPAN_MS = 8 * 60 * 60 * 1000;

function dateLine(date: ShowDate, locale: Locale, until: (date: string) => string): string {
  // A long-running session already on (an is_regular show): its end, not its past start.
  if (date.end_ts && Date.parse(date.start_ts) < Date.now() && Date.parse(date.end_ts) > Date.now()) {
    return until(formatDateLong(date.end_ts, date.tz, locale));
  }
  const end =
    date.end_ts && Date.parse(date.end_ts) - Date.parse(date.start_ts) <= MAX_SHOWN_SPAN_MS ? date.end_ts : null;
  return `${formatDateLong(date.start_ts, date.tz, locale)} · ${formatTimeRange(date.start_ts, end, date.tz, locale)}`;
}

/**
 * The feed's address often starts with the venue's name ("Coca-Cola Arena -
 * Dubai - United Arab Emirates"): then the address alone says both.
 */
function venueLine(venue: string | null, address: string | null): string {
  if (venue && address?.toLowerCase().includes(venue.toLowerCase())) return address;
  return [venue, address].filter(Boolean).join(' — ');
}

function ShowScreen({ show }: { show: EventShow }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { event, dates } = show;
  const past = isEventPast(event);
  const until = (date: string) => t('events.detail.until', { date });
  const title = localizedTitle(event, locale);
  const description = localizedDescription(event, locale);
  const copy = event.translations?.[locale];
  const highlights = copy?.highlights ?? [];
  const faq = copy?.faq ?? [];
  const image = usableImageUrl(event.image_url);
  const path = `events/${event.show_slug}`;
  const canonical = absoluteUrl(locale, path);
  const category = t(`events.categories.${event.category_id ?? 'other'}`, { defaultValue: t('events.categories.other') });
  const next = dates[0] ?? null;
  const ticketUrl = next?.ticket_url ?? event.ticket_url;
  const partner = partnerName(event.source);

  // A one-off show's <title> carries its date (that's what people search
  // with); a recurring one's can't name a single date.
  const titleDate = dates.length > 1 ? undefined : formatDateCompact(event.start_ts, event.tz, locale);
  // An ended show still describes its last date.
  const jsonLdDates: ShowDate[] = dates.length ? dates.slice(0, MAX_JSON_LD_DATES) : [event];
  const events = jsonLdDates.map((d) => eventJsonLd(event, d, SITE.regionName[locale], canonical, locale));

  const facts: { label: string; value: string }[] = [];
  if (next) facts.push({ label: t('events.detail.next'), value: dateLine(next, locale, until) });
  if (dates.length > 1) facts.push({ label: t('events.detail.dates'), value: t('events.detail.dateCount', { count: dates.length }) });
  if (event.venue_name) facts.push({ label: t('events.detail.venue'), value: event.venue_name });
  if (event.min_price != null) {
    facts.push({
      label: t('detail.price'),
      value: t('card.from', { price: formatEventPrice(Number(event.min_price), event.currency, locale) }),
    });
  }

  // "More events like this": the category's top shows, minus this one.
  const [related] = useState(() =>
    (getPrefetched<DbEvent[]>(LIST_BUCKET, relatedEventsSeedKey(event.category_id)) ?? [])
      .filter((e) => e.show_slug !== event.show_slug)
      .slice(0, RELATED_EVENTS),
  );
  // The hotels map opened from the side panel on desktop; closed on moving to another show.
  const [hotelsMapSrc, setHotelsMapSrc] = useState<string | null>(null);
  useEffect(() => setHotelsMapSrc(null), [event.show_slug]);
  const mapUrl =
    event.venue_lat != null && event.venue_lng != null
      ? `https://www.google.com/maps/search/?api=1&query=${event.venue_lat},${event.venue_lng}`
      : null;

  return (
    <Page>
      <PageHead
        title={seoTitle(localizedSeoTitle(event, locale), titleDate)}
        description={localizedSummary(event, locale) ?? truncate(description ?? title)}
        path={path}
        image={image}
        indexable={isIndexable(event)}>
        <script type="application/ld+json">{JSON.stringify(events.length === 1 ? events[0] : events)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [eventsCrumb(locale), { name: title }]))}
        </script>
        {faq.length ? <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(faq))}</script> : null}
        {/* Baked record, so the first client render matches this static HTML. */}
        <script id={prefetchElementId(EVENT_SHOW_BUCKET, event.show_slug)} type="application/json">
          {serializePrefetch(show)}
        </script>
        {seedScript(relatedEventsSeedKey(event.category_id))}
      </PageHead>

      <DetailLayout
        crumbs={[
          { label: t('nav.home'), href: localePath(locale) },
          { label: t('nav.events'), href: localePath(locale, 'events') },
        ]}
        imageUrl={image}
        imageAlt={title}
        category={category}
        title={title}
        facts={facts}
        stickyPanel
        wideImage
        action={
          past || !ticketUrl ? (
            <p>
              {t('events.detail.ended')}{' '}
              <NavLink href={localePath(locale, 'events')}>{t('events.detail.upcoming')}</NavLink>
            </p>
          ) : (
            <>
              <BookLink
                url={ticketUrl}
                label={t('events.detail.tickets')}
                meta={{ kind: 'event', source: event.source, id: next?.id ?? event.id, title, city: event.city_id, locale }}
              />
              {partner ? <small>{t('detail.via', { partner })}</small> : null}
              <EventHotels event={event} wideMap={{ src: hotelsMapSrc, setSrc: setHotelsMapSrc }} />
            </>
          )
        }>
        {/* The desktop hotels map: the side panel is too narrow and sticky for it (EventHotels). */}
        {hotelsMapSrc ? <HotelsMapFrame src={hotelsMapSrc} wide /> : null}

        {highlights.length ? (
          <section className={`${factsCss.section} ${css.highlights}`}>
            <h2 className={factsCss.heading}>{t('events.detail.highlights')}</h2>
            <ul className={factsCss.list}>
              {highlights.map((line) => (
                <li key={line} className={factsCss.yes}>
                  {line}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {(description ?? '')
          .split(/\n{2,}/)
          .filter(Boolean)
          .map((para, i) => (
            <p key={i}>{para}</p>
          ))}

        <div className={factsCss.wrap}>
          {dates.length > 1 ? (
            <section className={factsCss.section}>
              <h2 className={factsCss.heading}>{t('events.detail.datesTitle')}</h2>
              <ul className={factsCss.list}>
                {dates.slice(0, MAX_LISTED_DATES).map((d) => (
                  <li key={d.id} className={d.sold_out ? factsCss.no : factsCss.dot}>
                    {dateLine(d, locale, until)}
                    {d.sold_out ? ` · ${t('events.detail.soldOut')}` : ''}
                  </li>
                ))}
              </ul>
              {dates.length > MAX_LISTED_DATES ? (
                <p>{t('events.detail.moreDates', { count: dates.length - MAX_LISTED_DATES })}</p>
              ) : null}
            </section>
          ) : null}

          {event.venue_name || event.address ? (
            <section className={factsCss.section}>
              <h2 className={factsCss.heading}>{t('events.detail.venueTitle')}</h2>
              <p>
                {venueLine(event.venue_name, event.address)}
                {mapUrl ? (
                  <>
                    {' · '}
                    <a href={mapUrl} target="_blank" rel="noopener noreferrer">
                      {t('events.detail.map')}
                    </a>
                  </>
                ) : null}
              </p>
            </section>
          ) : null}

          {faq.length ? (
            <section className={factsCss.section}>
              <h2 className={factsCss.heading}>{t('events.detail.faqTitle')}</h2>
              <FaqList entries={faq} />
            </section>
          ) : null}
        </div>
      </DetailLayout>

      {related.length ? (
        <Section
          id="related-events"
          title={t('events.detail.related')}
          more={{ href: localePath(locale, 'events'), label: t('events.detail.allEvents') }}>
          <CardGrid>
            {related.map((e) => (
              <EventCard key={e.show_slug} event={e} />
            ))}
          </CardGrid>
        </Section>
      ) : null}
    </Page>
  );
}
